import React, { Component, createContext } from 'react';
import PropTypes from 'prop-types';
import { get, set, isEmpty } from 'lodash';
import Cookies from 'js-cookie';
import { canUseDOM } from 'exenv';
import EventModel from '../../../utils/tracking/models';
import axios from 'axios';

function getCookie(name) {
  if (!canUseDOM) return null;

  const value = Cookies.get(name);

  if (typeof value === 'undefined') {
    return null;
  }
  return value;
}

function transformUserAccessData(userAccess) {
  if (!userAccess) return undefined;

  if (
    !userAccess.entitlements ||
    Object.keys(userAccess.entitlements).length === 0
  ) {
    return {
      email: userAccess?.email || '',
      first_name: userAccess?.first_name || '',
      last_name: userAccess?.last_name || '',
      hasMagazineAccess: false,
      hasPaywallAccess: false
    };
  }

  // FC Magazine
  const hasMagazineAccess = userAccess?.entitlements?.['10']?.active || false;

  const hasPaywallAccess =
    userAccess?.entitlements?.['21']?.active || // FCEB
    userAccess?.entitlements?.['22']?.active || // FC Impact Council
    userAccess?.entitlements?.['23']?.active || // FC Premium
    false;

  return {
    email: userAccess?.email || '',
    first_name: userAccess?.first_name || '',
    last_name: userAccess?.last_name || '',
    hasMagazineAccess,
    hasPaywallAccess
  };
}

async function fetchUserData(uid) {
  const reqUrl = `https://omeda.mansueto.com/v4/user_data/lookup/fastcompany`;
  const response = await axios.post(
    reqUrl,
    {
      uid
    },
    {
      headers: {
        'Content-Type': 'application/json',
        'User-Agent': 'axios/mv-webapp'
      }
    }
  );

  const data = get(response, 'data', {});
  return { user: transformUserAccessData(data) };
}

export const UserContext = createContext({
  user: {},
  meter: {},
  activeOffer: {},
  journey: {},
  experience: {}
});

export class UserProvider extends Component {
  constructor(props) {
    super(props);
    this.state = {
      user: {},
      meter: {},
      activeOffer: {},
      journey: {},
      currentDrawer: 'NI',
      offerPageDetails: {},
      isUserValid: undefined,
      userAccessIds: [],
      hasMagazineAccess: undefined,
      hasPaywallAccess: false,
      experience: {},
      premiumPaywallActive: false
    };

    this.subscribedBcEvents = false;

    // binds
    this.logoutUser = this.logoutUser.bind(this);
    this.flushAllCookies = this.flushAllCookies.bind(this);
    this.hasPaywallAccess = this.hasPaywallAccess.bind(this);
  }

  componentDidMount() {
    const setCurrentUser = async () => {
      const currentUser = get(this.state, 'user', {});

      // Check for user ID in cookies or URL params to determine if user is logged in
      const params = new URLSearchParams(window.location.search);
      const encIdParams = params.get('oly_enc_id');

      const uid = getCookie('oly_enc_id') || encIdParams;

      if (!uid) return;
      if (!isEmpty(currentUser)) return;

      try {
        const { user } = await fetchUserData(uid);

        if (user && Object.keys(user).length > 0) {
          this.setState({
            user,
            isUserValid: true,
            hasMagazineAccess: user?.hasMagazineAccess || false,
            hasPaywallAccess: user?.hasPaywallAccess || false
          });
        }
      } catch (err) {
        console.error('Error fetching user data', err);
      }
    };

    setCurrentUser();
  }

  // /*------------------------------------------
  // * GETTERS & SETTERS FOR USER CONTEXT/STATE
  // * getUser, setUser, ...
  // --------------------------------------------*/
  setCurrentUser(user, event) {
    const { uid, valid } = user;

    if (!uid) return;

    this.updateUserLoginStatus();

    if (event) {
      // Track Login Event
      const loginEvent = new EventModel();
      loginEvent.addToDataLayer();
    }

    this.setState({
      user,
      isUserValid: valid
    });

    // Update the data layer object
    if (canUseDOM) {
      set(
        window,
        'digitalData.user[0].profile[0].profileInfo.profileID',
        uid || ''
      );
    }
  }

  updateUserLoginStatus() {
    const digitalDataUser = get(
      window,
      'digitalData.user[0].profile[0].attributes',
      false
    );
    if (canUseDOM && digitalDataUser) {
      window.digitalData.user[0].profile[0].attributes.isLoggedIn = this.isLoggedIn();
    }
  }

  logoutUser() {
    // Logout local application state
    this.setState(
      {
        user: {},
        userAccessIds: [],
        hasMagazineAccess: false,
        hasPaywallAccess: false,
        isLoggedIn: false,
        isUserValid: false
      },
      () => {
        // remove omeda cookies for logout
        document.cookie = `oly_enc_id=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=.fastcompany.com`;
        document.cookie = `oly_anon_id=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=.fastcompany.com`;

        // reload Omeda pageview on logout to ensure user is tracked as logged out
        if (typeof window?.olytics?.nextPageView === 'function') {
          window.olytics.nextPageView();
        }
      }
    );
  }

  flushAllCookies() {
    // refresh the page
    if (canUseDOM) {
      window.location.href = `${window.location.pathname}?jibboo`;
    }
  }

  // NOTE: `postId` is currently unused; access is determined via Omeda-based state.
  // eslint-disable-next-line no-unused-vars
  hasPaywallAccess(postId) {
    return get(this.state, 'hasPaywallAccess', false);
  }

  isLoggedIn() {
    const user = get(this.state, 'user', {});
    return !isEmpty(user);
  }

  render() {
    const finalState = {
      user: get(this.state, 'user', {}),
      meter: get(this.state, 'meter', {}),
      activeOffer: get(this.state, 'activeOffer', {}),
      journey: get(this.state, 'journey', {}),
      offerPageDetails: get(this.state, 'offerPageDetails', {}),
      isUserValid: get(this.state, 'isUserValid', false),
      hasMagazineAccess: get(this.state, 'hasMagazineAccess', false),
      experience: get(this.state, 'experience', {}),
      premiumPaywallActive: get(this.state, 'premiumPaywallActive', false),
      isLoggedIn: this.isLoggedIn(),
      logoutUser: this.logoutUser,
      // Piano integration removed -- no-op stubs for backward compatibility
      showScreen: () => {},
      showOffer: () => {},
      showUserAccount: () => {},
      retriggerPianoPost: () => {},
      retriggerPiano: () => {},
      flushAllCookies: this.flushAllCookies,
      setUpApplePay: () => {},
      hasPaywallAccess: this.hasPaywallAccess
    };

    return (
      <UserContext.Provider value={finalState}>
        {this.props.children}
      </UserContext.Provider>
    );
  }
}

UserProvider.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.array,
    PropTypes.object,
    PropTypes.shape({})
  ]).isRequired
};

export const UserConsumer = UserContext.Consumer;
